import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PwaInstallService } from './core/services/config/pwa-install.service';
import { AsyncPipe } from '@angular/common';
import { NgbToast } from '@ng-bootstrap/ng-bootstrap';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NotificationModalComponent } from './shared/components/modal/notification-modal/notification-modal.component';
import { ThemeToggleComponent } from './shared/components/theme-toggle/theme-toggle.component';
import { BackButtonComponent } from './shared/components/back-button/back-button.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, AsyncPipe, NotificationModalComponent, ThemeToggleComponent, BackButtonComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnDestroy, OnInit {
  title = 'ODONTO ARCO';
  isMobile: boolean = false;
  isTablet: boolean = false;
  isWeb: boolean = false;

  private destroy$ = new Subject<void>(); // Observable de finalización
  pwaInstallService = inject(PwaInstallService);
  isInstallable$ = this.pwaInstallService.installPrompt$;
  isLoading$ = this.pwaInstallService.loading$.pipe(takeUntil(this.destroy$)); // Tomar hasta que se destruya
  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.limpiarVelos());
  }

  /**
   * Saca los velos que hayan quedado colgados al cambiar de pantalla.
   *
   * CoreUI cuelga el fondo del offcanvas y de los modales del `<body>`, no
   * del componente. Si el componente se destruye con el panel abierto —abrir
   * el de WhatsApp en el login y apretar atrás— el velo queda ahí: invisible,
   * a pantalla completa, y comiéndose todos los toques. La pantalla parecía
   * congelada.
   *
   * Va acá y no en cada componente porque el mismo panel está repetido en
   * login y en nueva orden, y los modales tienen el mismo riesgo.
   */
  private limpiarVelos(): void {
    document
      .querySelectorAll('.offcanvas-backdrop, .modal-backdrop')
      .forEach((velo) => velo.remove());

    // El bloqueo de scroll que acompaña al velo también se queda pegado.
    document.body.classList.remove('offcanvas-open', 'modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }
  installPwa(): void {
    this.pwaInstallService.installPwa();
  }

  /**
   * Marca el <body> mientras hay un campo de texto enfocado.
   *
   * En móvil, al abrir el teclado el navegador desplaza el campo para dejarlo
   * a la vista, pero la barra de navegación inferior y la barra de acciones
   * de la orden están fijas y quedan justo encima: el campo de observaciones
   * terminaba tapado por el menú. Con la clase puesta, esas dos barras se
   * esconden mientras se escribe y vuelven al salir del campo.
   *
   * Se hace por foco y no por alto de ventana porque iOS no achica el
   * viewport al abrir el teclado: lo superpone, así que una media query de
   * altura no se entera.
   */
  @HostListener('document:focusin', ['$event'])
  alEnfocar(evento: FocusEvent): void {
    if (this.esCampoDeTexto(evento.target)) {
      document.body.classList.add('teclado-abierto');
    }
  }

  @HostListener('document:focusout', ['$event'])
  alDesenfocar(evento: FocusEvent): void {
    if (this.esCampoDeTexto(evento.target)) {
      document.body.classList.remove('teclado-abierto');
    }
  }

  private esCampoDeTexto(objetivo: EventTarget | null): boolean {
    const el = objetivo as HTMLElement | null;
    if (!el) return false;
    if (el.tagName === 'TEXTAREA') return true;
    if (el.tagName !== 'INPUT') return false;
    // Los checkbox y radios de los estudios no abren teclado: si contaran,
    // marcar un estudio escondería el botón de generar la orden.
    const tipo = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'file', 'range'].includes(tipo);
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([
        '(max-width: 576px)', // Celular
        // '(min-width: 577px) and (max-width: 768px)', // Tablet
        '(min-width: 577px)', // Escritorio
      ])
      .subscribe((result) => {
        this.isMobile = result.breakpoints['(max-width: 576px)'];
        // this.isTablet =
        //   result.breakpoints['(min-width: 577px) and (max-width: 768px)'];
        this.isWeb = result.breakpoints['(min-width: 577px)'];
        //console.log('que dispositivo es?');
        //console.log('movil: ' + this.isMobile);
        //console.log('tablet: ' + this.isTablet);
        //console.log('Desk: ' + this.isDesktop);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete(); // Completa el observable al destruir el componente
  }
}
